<template>
  <div :class="[additionalClasses, 'loyalty-table']">
    <div class="loyalty-table__paging-info row">
      <div class="col-12 col-m-6 mb-3">
        <h6 v-text="translate('lblPageStatus', { from: fromItem, to: toItem, total: totalItems })"></h6>
      </div>
      <div class="col-12 col-m-6 mb-3 text-right">
        <span v-text="translate('lblPageSize')"></span>
        <select v-model="itemsPerPage" class="ml-2" @change="updatePageSize">
          <option v-for="option in pageSizeOptions" :key="option"
                  v-text="option" :value="option"
          ></option>
        </select>
      </div>
    </div>
    <div class="table-box">
      <div class="table-box__inner">
        <table class="table">
          <thead>
          <tr>
            <table-head
              v-for="head in tableHeader" :key="head.name"
              :column="head.name"
              :is-sortable="head.isSortable"
              :type="head.type"
              :sort-order="sortOrder"
              @sortOrderChanged="changeSortOrder"
            ></table-head>
          </tr>
          </thead>
          <tbody>
          <tr
            v-for="(entry, index) in currentlyVisibleTableData" :key="index"
            :class="{
              'transaction-pending': entry.status && entry.status.toUpperCase() === 'PENDING',
              'transaction-declined': entry.status && entry.status.toUpperCase() === 'DECLINED',
            }"
          >
            <td
              v-for="column in tableHeader" :key="column.name"
              :class="entry[column.name].additionalClasses"
              v-html="entry[column.name].text || entry[column.name].value"
            ></td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>
    <paging-navigation
      :current-page="currentPage"
      :total-items="totalItems"
      :pageSize="itemsPerPage"
      :maxPages="maxPages"
      @pageChanged="goToPage"
    ></paging-navigation>
  </div>
</template>

<script>
import TableHead from './TableHead.vue';
import PagingNavigation from './PagingNavigation.vue';

export default {
  name: 'SortableTable',
  data: () => ({
    sortedTableData: [],
    currentlyVisibleTableData: [], // visible data according to pagination and sort order
    sortOrder: [], // contains columns used for sorting
    compareFunctions: { // used for sorting different data types
      date: (a, b) => {
        if (a.value > b.value) return 1;
        if (a.value < b.value) return -1;
        return 0;
      },
      alphanumeric(a, b) {
        return a.value.localeCompare(b.value, this.locale, { numeric: true });
      },
    },
    totalItems: 0, // number of entries returned by the api
    currentPage: 1, // the current active page
    itemsPerPage: 10,
  }),
  props: {
    /** *
     * tableHeader: {array} of objects containing the following properties:
     *  - name: {string} the column name
     *  - isSortable: {boolean} whether or not the table can be sorted by this column
     *  - type: {string} the type that determines which compare function has to be used for sorting
     */
    tableHeader: Array,
    /** *
     * tableData: {array} of objects
     *  - the key matches the name property of the tableHeader
     *  - the value is an object containing the following properties
     *    - value: the value is used for sorting and as fallback if no text is given
     *    - text: optional, is shown to the user as content of the table cell
     *    - additionalClasses: optional, CSS classes to be applied to the table cell
     */
    tableData: Array,
    additionalClasses: String,
    locale: String,
    pageSize: {
      type: Number,
      default: 10,
    },
    pageSizeOptions: {
      type: Array,
      default: () => [10],
    },
    maxPages: {
      type: Number,
      default: 3,
    },
  },
  computed: {
    fromItem() {
      return ((this.currentPage - 1) * this.itemsPerPage + 1);
    },
    toItem() {
      return Math.min(
        this.currentPage * this.itemsPerPage,
        this.sortedTableData.length,
      );
    },
  },
  components: {
    TableHead,
    PagingNavigation,
  },
  methods: {
    changeSortOrder(sort) {
      if (sort === null) {
        // no sorting to be applied → all sortings have to be removed
        this.sortOrder.splice(0, this.sortOrder.length);
        return;
      }
      // check whether column was already used for sorting
      const pos = this.sortOrder.findIndex((item) => item.by === sort.by);
      if (pos >= 0) {
        // already used → remove it for now, add again later with ascending order at beginning
        this.sortOrder.splice(pos, 1);
      }
      // insert most recent at beginning with asc order
      this.sortOrder.unshift(sort);
      this.applySortOrder();
      this.currentPage = 1;
      this.applyPagination();
    },
    applySortOrder() {
      const copy = [...this.tableData];
      for (let i = this.sortOrder.length - 1; i >= 0; i -= 1) {
        const sortOrderItem = this.sortOrder[i];
        // eslint-disable-next-line max-len
        // console.log(`sort by ${sortOrderItem.by} ${sortOrderItem.desc ? 'desc' : ''} using ${sortOrderItem.type} comparison`);
        copy.sort((a, b) => {
          const returnValue = this.compareFunctions[sortOrderItem.type].bind(this)(
            a[sortOrderItem.by],
            b[sortOrderItem.by],
          );
          return sortOrderItem.desc ? -1 * returnValue : returnValue;
        });
      }
      this.sortedTableData = copy;
    },
    applyPagination() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      this.currentlyVisibleTableData = this.sortedTableData.slice(start, end);
      this.$forceUpdate();
    },
    goToPage(page) {
      this.currentPage = page;
      this.applyPagination();
    },
    updatePageSize() {
      this.goToPage(1);
      this.$emit('update-page-size', this.itemsPerPage);
    },
  },
  created() {
    if (this.translations) {
      this.addDictionary(this.translations);
    }
    this.totalItems = this.tableData.length;
    this.itemsPerPage = this.pageSize;
    const sortInitially = this.tableHeader.find((th) => th.sortInitially);
    if (sortInitially) {
      this.sortOrder.push({
        by: sortInitially.name,
        desc: sortInitially.sortInitially,
        type: sortInitially.type,
      });
    }
    this.applySortOrder();
    this.applyPagination();
  },
};
</script>

<style lang="scss">
.loyalty-table {
  .loyalty-table__paging-info {
    justify-content: space-between;
    align-items: baseline;
    flex-wrap: wrap-reverse;

    select {
      width: auto;
      padding-right: 2.5rem;
    }
  }

  .point-value {
    &-positive {
      color: $color-kws-green-100;
    }

    &-negative {
      color: $color-kws-signalred-100;
    }
  }

  .table-box {
    @media (max-width: rem(568px)) {
      margin: 0 -1.6875rem;
      width: 100vw;
    }
  }

  table.table {
    th {
      background-color: $color-kws-light-gray-75;
      color: $color-kws-black-90;
      font-weight: inherit;
      position: relative;
      padding: rem(11px) rem(30px);
      line-height: rem(18px);
    }
    th, td {
      border-left: none;
    }
    td {
      background-color: unset;
      padding: rem(25px) rem(30px);
      white-space: nowrap;
    }
    tbody tr {
      border-bottom: 1px solid $color-kws-dark-gray-50;

      &.transaction-pending,
      &.transaction-declined {
        td {
          background-color: $color-kws-light-gray-50;

          &.touchpoint {
            color: $color-kws-orange;
          }
        }
      }
    }
  }
}
</style>
